// Copyright 2025 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later
"use client";

import { useActiveOrg } from "@/hooks/useActiveOrg";
import { classNames } from "@/utils/common";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import type { ReactNode } from "react";
import useDecodedParams from "../../hooks/useDecodedParams";
import UserNav from "../navigation/UserNav";
import EntityProviderImage from "./EntityProviderImage";
import dynamic from "next/dynamic";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

const OrganizationDropDown = dynamic(() => import("../OrganizationDropDown"), {
  ssr: false,
});

const levelMapping: { [idx: number]: string } = {
  1: "root",
  2: "organization",
  3: "group",
  4: "repo",
};

function getLevelMapping(level: number) {
  const mapping = levelMapping[level];
  if (mapping === undefined) {
    throw new Error("invalid mapping for level: " + level);
  }
  return mapping;
}

import type { MenuItem } from "@/types/view/navigation";

interface Props {
  Title: ReactNode;
  menu: Array<MenuItem> | null;
  z: number;
}
export default function DynamicHeader({ Title, menu, z }: Props) {
  const activeOrg = useActiveOrg();
  const pathname = usePathname();
  const params = useDecodedParams();
  const levelStr = useMemo(() => "level-" + getLevelMapping(z), [z]);

  const slug = params.organizationSlug || activeOrg?.slug || "";
  return (
    <header
      style={{ zIndex: 20 + z }}
      className={classNames(
        "absolute top-0 left-0 right-0 z-20 flex min-h-[109px] items-center justify-between bg-header px-4 pt-5 sm:px-6 lg:px-8",
        Boolean(menu) ? "pb-3" : "pb-5",
        levelStr,
      )}
    >
      <div className="mx-auto w-full max-w-screen-2xl">
        <div className="flex flex-row items-center gap-4">
          <Link href={`/${slug}`}>
            <EntityProviderImage provider={slug} />
          </Link>
          <div>
            <OrganizationDropDown />
          </div>
          <div className="flex w-full flex-row items-center justify-between">
            <h1 className="font-display whitespace-nowrap text-lg font-semibold leading-7 text-header-foreground">
              {Title}
            </h1>

            <UserNav />
          </div>
        </div>
        {menu && (
          <div
            data-tour="menu"
            className="flex flex-row items-end gap-6 text-sm"
          >
            {menu.map((item) =>
              item.children && item.children.length > 0 ? (
                <DropdownMenu key={item.title}>
                  <div
                    className="cursor-pointer relative hover:no-underline outline-none"
                    data-testid={item.testId}
                  >
                    {(item.isActive ||
                      item.children.some(
                        (child) => child.isActive || pathname == child.href,
                      )) && (
                      <div className="absolute -bottom-3 -left-2 -right-2 h-0.5 bg-primary" />
                    )}
                    <div className="mt-4 flex flex-row items-center gap-1">
                      <Link
                        href={item.href}
                        className="flex flex-row items-center gap-1 hover:no-underline"
                      >
                        <item.Icon className="h-5 w-5 text-gray-400" />
                        <span className="text-header-foreground">
                          {item.title}
                        </span>
                      </Link>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="outline-none"
                          data-testid={
                            item.testId ? `${item.testId}-chevron` : undefined
                          }
                        >
                          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      </DropdownMenuTrigger>
                    </div>
                  </div>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem key={item.title} asChild>
                      <Link
                        href={item.href}
                        data-testid={item.testId}
                        className="flex !text-foreground flex-row items-center gap-2"
                      >
                        <item.Icon className="h-4 w-4 text-gray-400" />
                        <span>{item.title}</span>
                      </Link>
                    </DropdownMenuItem>
                    {item.children.map((child) => (
                      <DropdownMenuItem key={child.title} asChild>
                        <Link
                          href={child.href}
                          data-testid={child.testId}
                          className="flex !text-foreground flex-row items-center gap-2"
                        >
                          <child.Icon className="h-4 w-4 text-gray-400" />
                          <span>{child.title}</span>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  className={classNames(
                    "cursor-pointer relative hover:no-underline",
                  )}
                  key={item.title}
                  href={item.href}
                  data-testid={item.testId}
                >
                  {(item.isActive || pathname == item.href) && (
                    <div className="absolute -bottom-3 -left-2 -right-2 h-0.5 bg-primary" />
                  )}
                  <div className="mt-4 flex flex-row items-center gap-1">
                    <item.Icon className="h-5 w-5 text-gray-400" />
                    <span className="text-header-foreground">{item.title}</span>
                  </div>
                </Link>
              ),
            )}
          </div>
        )}
      </div>
    </header>
  );
}
